import React, {useEffect, useMemo, useState} from "react";
import {AiOutlineLoading3Quarters} from "react-icons/ai";

const STEPS = [
  {id: "welcome", label: "Welcome"},
  {id: "tour", label: "Where to look"},
  {id: "underhood", label: "Under the hood"},
  {id: "signin", label: "Sign in"},
];

const TOUR = [
  {
    location: "Homepage",
    items: [
      {
        title: '"For You" rail',
        body: "Top of the page. Re-ranks as you read, click, and upvote - refresh after a few interactions to watch the order shift.",
      },
      {
        title: "Categories",
        body: "Below the rail. Both the order of the categories and the stories inside each one are personalised - the whole layout adapts.",
      },
      {
        title: "Auto sections",
        body: "Further down. Brand-new sections that NeuronSearchLab invents from your recent behaviour - both the title and the contents.",
      },
    ],
  },
  {
    location: "Any article page",
    items: [
      {
        title: '"Similar content" rail',
        body: "Below the article body. Related items, but re-ranked by your taste - two readers see different lists.",
      },
      {
        title: "Comments",
        body: "Bottom of the page. Personalised per reader, with a business rule pinning a contrarian take at slot 3 of every thread.",
      },
    ],
  },
  {
    location: "/explore",
    items: [
      {
        title: "Full-screen feed",
        body: 'The "For You" rail rendered as a swipeable, one-story-at-a-time feed - same NSL context, different surface.',
      },
    ],
  },
];

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export default function OnboardingModal({onComplete}) {
  const [stepIndex, setStepIndex] = useState(0);
  const [email, setEmail] = useState("");
  const [consentGiven, setConsentGiven] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem("onboardingComplete");
    if (completed === "true") onComplete();
  }, [onComplete]);

  const subscribeToMailchimp = useMemo(
    () => () =>
      new Promise((resolve, reject) => {
        const callbackName = `mc_callback_${Date.now()}`;
        const url = `https://neuronsearchlab.us11.list-manage.com/subscribe/post-json?u=b58baa65c5a28344f62b21e7c&id=4388d1eaef&c=${callbackName}`;

        window[callbackName] = function (data) {
          delete window[callbackName];
          if (data.result === "success") resolve();
          else reject(new Error(data.msg || "Subscription failed."));
        };

        const script = document.createElement("script");
        script.src = `${url}&EMAIL=${encodeURIComponent(email.trim())}`;
        script.onload = () => document.body.removeChild(script);
        script.onerror = () => {
          delete window[callbackName];
          document.body.removeChild(script);
          reject(new Error("Network error."));
        };
        document.body.appendChild(script);
      }),
    [email]
  );

  const finish = () => {
    localStorage.setItem("onboardingComplete", "true");
    onComplete(email.trim());
  };

  const handleNext = async () => {
    setError("");

    if (stepIndex < STEPS.length - 1) {
      setStepIndex(stepIndex + 1);
      return;
    }

    if (!isValidEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!consentGiven) {
      setError("Please confirm consent to continue.");
      return;
    }

    setIsSubmitting(true);
    try {
      await subscribeToMailchimp();
      finish();
    } catch (err) {
      // Mailchimp returns "already subscribed" as an error - treat it as success
      // so a returning demo visitor isn't blocked.
      if (/already subscribed/i.test(err.message)) {
        finish();
        return;
      }
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setError("");
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
  };

  const isLastStep = stepIndex === STEPS.length - 1;
  const primaryLabel = isLastStep ? "Start exploring" : "Continue";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/70 px-4 py-6 backdrop-blur-[2px]">
      <div className="animate-overlay-in flex max-h-[calc(100dvh-3rem)] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-rule bg-paper shadow-overlay">
        {/* Progress */}
        <div className="px-6 pt-6">
          <div className="flex items-center justify-between mb-1">
            <span className="font-sans text-kicker uppercase tracking-[0.09em] text-masthead">
              {STEPS[stepIndex].label}
            </span>
            <span className="font-sans text-kicker uppercase tracking-[0.09em] text-ink-muted">
              {stepIndex + 1} / {STEPS.length}
            </span>
          </div>
          <div className="flex gap-1.5">
            {STEPS.map((step, idx) => (
              <div
                key={step.id}
                className={`h-[3px] flex-1 transition-colors duration-300 ${
                  idx <= stepIndex
                    ? "bg-masthead"
                    : "bg-rule"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-6 sm:py-8 flex-1 overflow-y-auto min-h-0">
          {stepIndex === 0 && (
            <div>
              <h2 className="font-serif text-h1 font-bold tracking-[-0.021em] text-ink">
                Welcome to the Pembury News demo
              </h2>
              <p className="mt-3 text-standfirst text-ink-secondary">
                A live showcase for{" "}
                <span className="font-medium text-ink">
                  NeuronSearchLab
                </span>
                's recommendation engine. Every rail, comment thread, and
                auto-generated section on this site is personalised in real
                time - built from a single, simple integration.
              </p>
              <p className="mt-4 text-body text-ink-tertiary">
                Spend a few minutes reading and clicking - the next two
                steps map out where to look, and how to peek under the
                hood whenever you want.
              </p>
            </div>
          )}

          {stepIndex === 1 && (
            <div>
              <h2 className="font-serif text-h2 font-bold tracking-[-0.016em] text-ink">
                Where to look
              </h2>
              <p className="mt-2 text-body text-ink-secondary">
                A quick map of every personalised surface, grouped by
                where you'll find it. All powered by the same NSL
                engine - each showing a different shape of
                personalisation.
              </p>
              <div className="mt-5 space-y-5">
                {TOUR.map((group) => (
                  <div key={group.location}>
                    <p className="mb-2 font-sans text-kicker font-semibold uppercase tracking-[0.14em] text-pencil">
                      {group.location}
                    </p>
                    <ul className="space-y-2">
                      {group.items.map((item) => (
                        <li
                          key={item.title}
                          className="rounded-md border border-rule bg-surface-1 px-4 py-3"
                        >
                          <p className="font-sans text-body font-semibold text-ink">
                            {item.title}
                          </p>
                          <p className="mt-0.5 text-meta leading-relaxed text-ink-tertiary">
                            {item.body}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stepIndex === 2 && (
            <div>
              <h2 className="font-serif text-h2 font-bold tracking-[-0.016em] text-ink">
                See under the hood
              </h2>
              <p className="mt-2 text-body text-ink-secondary">
                Every personalised surface has a small{" "}
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-current text-[10px] align-middle">
                  i
                </span>{" "}
                next to its title. Hover for a plain-English
                explanation of what's happening.
              </p>

              <div className="mt-5 rounded-md border border-pencil/25 bg-pencil-muted px-4 py-4">
                <div className="flex items-start gap-3">
                  <span className="inline-flex shrink-0 items-center justify-center rounded-sm border border-pencil/30 bg-paper px-2 py-1 font-mono text-meta text-pencil">
                    {"</>"}
                  </span>
                  <div>
                    <p className="font-sans text-body font-semibold text-ink">
                      Flip on dev mode
                    </p>
                    <p className="mt-1 text-meta leading-relaxed text-ink-secondary">
                      Tap the{" "}
                      <code className="font-mono text-meta text-pencil">
                        {"</>"}
                      </code>{" "}
                      icon in the top nav to switch every tooltip into
                      a technical readout - exact NSL context IDs,
                      active rules, scope filters, pipeline overrides,
                      and any client-side guards.
                    </p>
                    <p className="mt-2 text-meta leading-relaxed text-ink-secondary">
                      Flip it off any time to go back to the
                      customer-facing copy.
                    </p>
                  </div>
                </div>
              </div>

              <p className="mt-4 text-meta leading-relaxed text-ink-muted">
                Bonus: every event type the engine reads - clicks,
                upvotes, dwell, scroll-depth, even silent
                impressions - is configured in the NSL admin
                dashboard. Nothing is hard-coded; the same approach
                drops into any product.
              </p>
            </div>
          )}

          {stepIndex === 3 && (
            <div>
              <h2 className="font-serif text-h2 font-bold tracking-[-0.016em] text-ink">
                Sign in to begin
              </h2>
              <p className="mt-2 text-body text-ink-secondary">
                Your email becomes your user ID for the demo, so the
                personalisation stays consistent across sessions.
              </p>

              <label
                htmlFor="onboarding-email"
                className="mb-1.5 mt-6 block font-sans text-kicker font-semibold uppercase tracking-[0.09em] text-ink-tertiary"
              >
                Email
              </label>
              <input
                id="onboarding-email"
                type="email"
                autoComplete="email"
                className="h-11 w-full rounded-md border border-[hsl(var(--control-border))] bg-[hsl(var(--control-bg))] px-3 font-sans text-body text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !isSubmitting) handleNext();
                }}
                disabled={isSubmitting}
              />

              <label className="mt-4 flex cursor-pointer items-start gap-2.5 text-body text-ink-secondary">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 rounded-sm border-[hsl(var(--control-border))] text-masthead focus:ring-ring"
                  checked={consentGiven}
                  onChange={(e) => setConsentGiven(e.target.checked)}
                  disabled={isSubmitting}
                />
                <span className="leading-snug">
                  I'm happy for NeuronSearchLab to use this email as a
                  personalisation identifier and to send occasional
                  product updates.
                </span>
              </label>

              {error && (
                <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-body text-destructive">
                  {error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-rule px-6 pb-6 pt-4">
          <button
            type="button"
            onClick={handleBack}
            disabled={stepIndex === 0 || isSubmitting}
            className="font-sans text-body font-medium text-ink-tertiary transition-colors duration-150 ease-out hover:text-ink disabled:cursor-default disabled:opacity-0"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={isSubmitting}
            className="inline-flex h-11 min-w-[140px] items-center justify-center gap-2 rounded-md bg-ink px-6 font-sans text-body font-semibold text-paper transition-[background-color,transform] duration-150 ease-out hover:bg-ink/88 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {isSubmitting && (
              <AiOutlineLoading3Quarters className="animate-spin" />
            )}
            {isSubmitting ? "Signing in…" : primaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
